import React, { Component } from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { Flex, H2, H3, TextBox } from '@revolut/ui-kit'
import { FormikActions, FormikProps } from 'formik'
import { Check, Cross } from '@revolut/icons'
import { Credentials } from 'containers/Credentials'
import { PinCode } from 'containers/PinCode'
import { PushNotification } from 'containers/PushNotification'
import { ConfirmationCode } from 'containers/ConfirmationCode'
import { Pockets } from 'containers/Pockets'
import { MultiStepForm } from 'components/MultiStepForm'
import { Slide } from 'components/Slide'

import type {
  AuthorizationBody,
  ConsentBody,
  RejectionBody,
  RetailAuthenticationBody,
  RetailVerificationBody,
} from 'services/api'

import {
  getFromLocalStorage,
  getParameterByName,
  removeTokensFromStorage,
} from 'helpers/common'
import { getApplicationDisplayData } from 'helpers/applicationData'
import { Consents, FetchStates, LocalStorageKeys, Screens } from 'constants/common'
import {
  authenticateAction,
  authorizeConsentAction,
  rejectConsentAction,
  checkAuthAction,
  fetchApplicationDataAction,
  fetchConsentDetailsAction,
  verifyAuthAction,
} from 'thunks'
import { setActiveScreenAction, setErrorAction } from 'reducers/generalReducer'
import type { GeneralState } from 'reducers/generalReducer'
import { getSchema } from 'schemas'
import type { AppState } from 'store'
import { Loader } from 'components/Loader'
import type { AccessToken, Error } from 'types/common'
import { PaymentConfirmation } from 'containers/Payment'
import { FundsConfirmation } from 'containers/FundsConfirmation'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { notReachable } from './helpers/notReachable'

type SlideFormikActions = Pick<
  FormikActions<any>,
  'setFieldValue' | 'setErrors' | 'setSubmitting'
>

type OwnProps = {
  general: GeneralState
  verify: (data: RetailVerificationBody) => void
  authenticate: (
    data: RetailAuthenticationBody,
    slideFormikActions: SlideFormikActions,
  ) => void
  authorize: (data: AuthorizationBody) => void
  reject: (data: RejectionBody) => void
  setActiveScreen: (screen: string) => void
  setError: (error: Error) => void
  checkAuth: () => void
  fetchApplicationData: (clientId: string) => void
  fetchConsentDetails: (data: ConsentBody, accessToken: AccessToken) => void
}

const VALID_SCOPES: Record<string, Consents> = {
  accounts: Consents.ACCOUNT,
  payments: Consents.PAYMENT,
  fundsconfirmations: Consents.FUNDS_CONFIRMATION,
}

const INITIAL_VALUES = {
  phone: {
    code: '+44',
    number: '',
  },
  password: '',
  tokenId: '',
  tokenExpiresIn: 0,
  code: getFromLocalStorage(LocalStorageKeys.VERIFICATION_TOKEN),
  email: '',
}

const CONSENT_PARAMS = {
  response_type: getParameterByName('response_type'),
  client_id: getParameterByName('client_id'),
  redirect_uri: getParameterByName('redirect_uri'),
  scope: getParameterByName('scope'),
  state: getParameterByName('state'),
  nonce: getParameterByName('nonce'),
  request: getParameterByName('request'),
  response_mode: getParameterByName('response_mode') || undefined,
}

const MISSING_CONSENT_SCOPE_ERROR: Error = {
  message: 'Invalid authorisation request',
  description: 'Missing or unsupported scope query parameter.',
}

/**
 * This this screen might change over time, and should be extracted at later point.
 */
const ErrorScreen: React.FC<{
  error?: Error
}> = ({ error = { message: '' } }) => (
  <Slide>
    <Flex mx='auto' flexDirection='column' alignItems='flex-start'>
      <Cross size='120' color='error' />
      <H3 mt='2rem' textAlign='center'>
        {error.message}
      </H3>
      <TextBox color='grey-50' mt='1rem'>
        {error.description}
      </TextBox>
    </Flex>
  </Slide>
)

const SuccessScreen: React.FC<{ appName: string }> = ({ appName }) => (
  <Slide>
    <Flex mx='auto' flexDirection='column' alignItems='flex-start'>
      <Check size='120' color='primary' />
      <H2 mt='2rem' mb='.5rem'>
        Access granted
      </H2>
      <TextBox color='grey-35'>You will be now redirected back to {appName}</TextBox>
    </Flex>
  </Slide>
)

const SuccessRejectionScreen: React.FC<{ appName: string }> = ({ appName }) => (
  <Slide>
    <Flex mx='auto' flexDirection='column' alignItems='flex-start'>
      <Check size='120' color='primary' />
      <H2 mt='2rem' mb='.5rem'>
        Access rejected
      </H2>
      <TextBox color='grey-35'>You will be now redirected back to {appName}</TextBox>
    </Flex>
  </Slide>
)

type Props = OwnProps & RouteComponentProps

class App extends Component<Props> {
  constructor(props: Props) {
    super(props)

    this.props.fetchApplicationData(CONSENT_PARAMS.client_id || '')

    const scope = this.getConsent(CONSENT_PARAMS.scope || '')
    if (!scope) {
      return
    }
    this.props.checkAuth()
    if (props.general.data.accessToken) {
      this.fetchConsent(props.general.data.accessToken)
    }
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.handleWindowUnload)
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.general.data.activeScreen !== prevProps.general.data.activeScreen) {
      window.scrollTo(0, 0)
    }
    if (this.props.general.data.accessToken !== prevProps.general.data.accessToken) {
      this.fetchConsent(this.props.general.data.accessToken)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleWindowUnload)
  }

  handleWindowUnload = () => {
    removeTokensFromStorage()
  }

  fetchConsent = (accessToken: AccessToken) => {
    const scope = this.getConsent(CONSENT_PARAMS.scope || '')
    if (scope) {
      this.props.fetchConsentDetails(
        {
          client_id: CONSENT_PARAMS.client_id,
          request: CONSENT_PARAMS.request,
          consent: scope,
        },
        accessToken,
      )
    }
  }

  // This submit path is only reachable from the embedded Personal form shown on
  // sandbox. Non-sandbox Personal and Business flows are regular anchor links.
  handleCredentialsSubmit = () => {
    const scope = this.getConsent(CONSENT_PARAMS.scope || '')

    if (!scope) {
      this.props.setError(MISSING_CONSENT_SCOPE_ERROR)
      this.props.setActiveScreen(Screens.ERROR)
      return
    }

    this.props.setActiveScreen(Screens.PIN)
  }

  handleNavigationToCredentials = () => {
    this.props.setActiveScreen(Screens.CREDENTIALS)
  }

  handleAccountAuthorization = (data: {
    verify_token: string
    consented_pockets?: string[]
  }) => {
    this.props.authorize({
      ...data,
      ...CONSENT_PARAMS,
    })
  }

  handleConsentRejection = () => {
    this.props.reject({
      client_id: CONSENT_PARAMS.client_id,
      redirect_uri: CONSENT_PARAMS.redirect_uri,
      request: CONSENT_PARAMS.request,
      state: CONSENT_PARAMS.state,
      response_mode: CONSENT_PARAMS.response_mode,
    })
  }

  getConsent = (scope: string): Consents | null => {
    const [consent = null] = Object.keys(VALID_SCOPES)
      .filter((val) => scope.includes(val))
      .map((val) => VALID_SCOPES[val])

    return consent
  }

  render() {
    const {
      general: {
        data: {
          activeScreen,
          consentFetchState,
          authCheckState,
          error,
          applicationData: applicationDataState,
        },
      },
    } = this.props

    const { fetchState } = applicationDataState

    switch (fetchState) {
      case FetchStates.NOT_STARTED:
      case FetchStates.PENDING:
        return (
          <Flex alignItems='center' minHeight='100vh'>
            <Loader />
          </Flex>
        )
      case FetchStates.FAILURE:
        return <ErrorScreen error={applicationDataState.error} />
      case FetchStates.SUCCESS: {
        if (
          !error &&
          (consentFetchState === FetchStates.PENDING ||
            authCheckState === FetchStates.PENDING)
        ) {
          return (
            <Flex alignItems='center' minHeight='100vh'>
              <Loader />
            </Flex>
          )
        }

        const { appName } = getApplicationDisplayData({
          appName: applicationDataState.data.name,
          clientId: CONSENT_PARAMS.client_id,
        })

        const applicationData = applicationDataState.data

        return (
          <MultiStepForm
            initialValues={INITIAL_VALUES}
            schema={getSchema(activeScreen)}
            steps={{
              [Screens.CREDENTIALS]: {
                slide: Credentials,
                slideProps: {
                  applicationData,
                },
                onSubmit: this.handleCredentialsSubmit,
              },
              [Screens.PIN]: {
                slide: PinCode,
                onSubmit: ({ password, phone }, formikProps: FormikProps<any>) => {
                  const phoneNumber = `${phone.code}${phone.number}`
                  const payload: RetailAuthenticationBody = {
                    phone: phoneNumber,
                    password,
                    type: 'APP',
                  }

                  this.props.authenticate(payload, formikProps)
                },
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                },
              },
              [Screens.PUSH_NOTIFICATION]: {
                slide: PushNotification,
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                },
              },
              [Screens.CONFIRMATION]: {
                slide: ConfirmationCode,
                onSubmit: ({ phone, code }) => {
                  const phoneNumber = `${phone.code}${phone.number}`
                  const confirmationCode = code.replace(/[^\d]/g, '')
                  const payload = { verify_token: confirmationCode, phone: phoneNumber }

                  this.props.verify(payload)
                },
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                },
              },
              [Screens.POCKETS]: {
                slide: Pockets,
                onSubmit: ({ code, pockets }) => {
                  const confirmationCode = code.replace(/[^\d]/g, '')
                  this.handleAccountAuthorization({
                    verify_token: confirmationCode,
                    consented_pockets: pockets,
                  })
                },
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                  onClose: this.handleConsentRejection,
                },
              },
              [Screens.FUNDS_CONFIRMATION]: {
                slide: FundsConfirmation,
                onSubmit: ({ code }) => {
                  const confirmationCode = code.replace(/[^\d]/g, '')
                  this.handleAccountAuthorization({
                    verify_token: confirmationCode,
                  })
                },
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                  onClose: this.handleConsentRejection,
                },
              },
              [Screens.PAYMENT]: {
                slide: PaymentConfirmation,
                onSubmit: ({ code, pocket }) => {
                  const confirmationCode = code.replace(/[^\d]/g, '')
                  this.handleAccountAuthorization({
                    verify_token: confirmationCode,
                    consented_pockets: pocket ? [pocket] : [],
                  })
                },
                slideProps: {
                  applicationData,
                  onBack: this.handleNavigationToCredentials,
                  onClose: this.handleConsentRejection,
                },
              },
              [Screens.ERROR]: {
                slide: () => <ErrorScreen error={error} />,
                onSubmit: () => window.location.reload(),
              },
              [Screens.SUCCESS]: {
                slide: () => <SuccessScreen appName={appName} />,
              },
              [Screens.SUCCESS_REJECT]: {
                slide: () => <SuccessRejectionScreen appName={appName} />,
              },
            }}
            active={activeScreen}
          />
        )
      }
      /* istanbul ignore next */
      default:
        return notReachable(fetchState)
    }
  }
}

const mapStateToProps = ({ general }: AppState) => ({
  general,
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  verify: (data: RetailVerificationBody) => dispatch(verifyAuthAction(data)),
  authenticate: (
    data: RetailAuthenticationBody,
    slideFormikActions: SlideFormikActions,
  ) => dispatch(authenticateAction(data, slideFormikActions)),
  setActiveScreen: (screen: string) => dispatch(setActiveScreenAction(screen)),
  setError: (error: Error) => dispatch(setErrorAction(error)),
  authorize: (data: AuthorizationBody) => dispatch(authorizeConsentAction(data)),
  reject: (data: RejectionBody) => dispatch(rejectConsentAction(data)),
  checkAuth: () => dispatch(checkAuthAction()),
  fetchConsentDetails: (data: ConsentBody, accessToken: AccessToken) =>
    dispatch(fetchConsentDetailsAction(data, accessToken)),
  fetchApplicationData: (clientId: string) =>
    dispatch(fetchApplicationDataAction(clientId)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App) as any)
